"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trash2,
  RotateCcw,
  X,
  Loader2,
  RefreshCw,
  FileText,
  Circle,
  LineChart,
  AlertTriangle,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { formatRelativeTime } from "@/lib/timezone";

type TrashEntity = "issues" | "docs" | "progress_cards";

interface TrashRow {
  id: string;
  identifier?: string;
  title: string;
  status?: string;
  icon?: string;
  deleted_at: string;
}

interface TrashPayload {
  issues: TrashRow[];
  docs: TrashRow[];
  progress_cards: TrashRow[];
}

/**
 * Slide-in panel for soft-deleted tasks, docs, and progress cards. The
 * writer-only trigger lives under Sidebar > More; permanent purge controls
 * are only rendered for workspace admins.
 */
export function TrashPanel({
  open,
  onOpenChange,
  canAdmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canAdmin: boolean;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const [data, setData] = useState<TrashPayload>({
    issues: [],
    docs: [],
    progress_cards: [],
  });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/trash`);
      const json = (await res.json()) as TrashPayload;
      setData(json);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeWorkspaceId]);

  async function action(
    entity: TrashEntity,
    id: string,
    method: "POST" | "DELETE"
  ) {
    setBusy(`${method}:${id}`);
    await fetch(
      `/api/workspaces/${activeWorkspaceId}/trash/${entity}/${id}`,
      { method }
    );
    setBusy(null);
    void load();
  }

  const total = data.issues.length + data.docs.length + data.progress_cards.length;

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              onClick={() => onOpenChange(false)}
            />
            <motion.aside
              key="panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="fixed top-0 right-0 bottom-0 w-full md:w-[480px] z-50 bg-[var(--bg)] border-l border-[var(--border)] shadow-2xl flex flex-col"
            >
              <div className="flex items-center gap-2 px-4 h-12 border-b border-[var(--border)] bg-[var(--bg-secondary)]">
                <Trash2 size={14} className="text-[var(--fg-tertiary)]" />
                <h3 className="text-[13.5px] font-semibold">Trash</h3>
                <span className="text-[11px] text-[var(--fg-muted)]">
                  {total} item{total === 1 ? "" : "s"}
                </span>
                <button
                  onClick={() => void load()}
                  className="ml-auto w-7 h-7 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
                  title="Refresh"
                >
                  <RefreshCw size={12} />
                </button>
                <button
                  onClick={() => onOpenChange(false)}
                  className="w-7 h-7 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
                  title="Close"
                >
                  <X size={14} />
                </button>
              </div>

              {loading ? (
                <div className="flex-1 flex items-center justify-center text-[12.5px] text-[var(--fg-muted)]">
                  <Loader2 size={14} className="animate-spin mr-2" /> Loading…
                </div>
              ) : total === 0 ? (
                <div className="flex-1 flex items-center justify-center px-6 text-center">
                  <div>
                    <Trash2 size={28} className="mx-auto text-[var(--fg-muted)] mb-3" />
                    <p className="text-[13px] text-[var(--fg-tertiary)]">
                      Empty. Nothing to restore here.
                    </p>
                    <p className="text-[11.5px] text-[var(--fg-muted)] mt-1">
                      Deleted items appear here automatically.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto scrollbar-thin">
                  <Section
                    title="Tasks"
                    icon={<Circle size={11} />}
                    rows={data.issues}
                    entity="issues"
                    busy={busy}
                    canAdmin={canAdmin}
                    onRestore={(id) => action("issues", id, "POST")}
                    onPurge={(id) => action("issues", id, "DELETE")}
                  />
                  <Section
                    title="Docs"
                    icon={<FileText size={11} />}
                    rows={data.docs}
                    entity="docs"
                    busy={busy}
                    canAdmin={canAdmin}
                    onRestore={(id) => action("docs", id, "POST")}
                    onPurge={(id) => action("docs", id, "DELETE")}
                  />
                  <Section
                    title="Progress cards"
                    icon={<LineChart size={11} />}
                    rows={data.progress_cards}
                    entity="progress_cards"
                    busy={busy}
                    canAdmin={canAdmin}
                    onRestore={(id) => action("progress_cards", id, "POST")}
                    onPurge={(id) => action("progress_cards", id, "DELETE")}
                  />
                </div>
              )}

              <div className="px-4 py-2.5 border-t border-[var(--border)] bg-[var(--bg-secondary)] text-[10.5px] text-[var(--fg-muted)] flex items-center gap-1.5">
                <AlertTriangle size={10} />
                <span>
                  Restore returns the item to its original view.
                  {canAdmin && " Purge is permanent."}
                </span>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function Section({
  title,
  icon,
  rows,
  busy,
  canAdmin,
  onRestore,
  onPurge,
}: {
  title: string;
  icon: React.ReactNode;
  rows: TrashRow[];
  entity: TrashEntity;
  busy: string | null;
  canAdmin: boolean;
  onRestore: (id: string) => void;
  onPurge: (id: string) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="border-b border-[var(--border)]">
      <div className="px-4 py-2 flex items-center gap-2 bg-[var(--bg-secondary)] sticky top-0 z-10">
        <span className="text-[var(--fg-tertiary)]">{icon}</span>
        <span className="text-[11px] uppercase tracking-wide font-medium text-[var(--fg-tertiary)]">
          {title}
        </span>
        <span className="text-[10.5px] text-[var(--fg-muted)]">{rows.length}</span>
      </div>
      <div className="divide-y divide-[var(--border)]">
        {rows.map((r) => {
          const restoring = busy === `POST:${r.id}`;
          const purging = busy === `DELETE:${r.id}`;
          return (
            <div
              key={r.id}
              className="flex items-center gap-2 px-4 py-2 hover:bg-[var(--bg-hover)]"
            >
              {r.icon && <span className="text-[14px]">{r.icon}</span>}
              {r.identifier && (
                <span className="text-[11px] font-mono text-[var(--fg-muted)] w-[60px] shrink-0">
                  {r.identifier}
                </span>
              )}
              <span className="flex-1 min-w-0 truncate text-[12.5px]">{r.title}</span>
              <span
                className="text-[10.5px] text-[var(--fg-muted)] shrink-0"
                title={new Date(r.deleted_at).toISOString()}
              >
                {formatRelativeTime(r.deleted_at)}
              </span>
              <button
                onClick={() => onRestore(r.id)}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-[var(--fg-secondary)] hover:bg-[var(--bg-active)] disabled:opacity-40 transition-colors"
                title="Restore"
              >
                {restoring ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <RotateCcw size={10} />
                )}
                Restore
              </button>
              {canAdmin && (
                <button
                  onClick={() => {
                    if (
                      confirm(
                        `Permanently delete "${r.title}"? This cannot be undone.`
                      )
                    )
                      onPurge(r.id);
                  }}
                  disabled={!!busy}
                  className="w-6 h-6 rounded flex items-center justify-center text-[var(--fg-tertiary)] hover:text-[var(--red)] hover:bg-[var(--bg-active)] disabled:opacity-40 transition-colors"
                  title="Delete forever"
                >
                  {purging ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : (
                    <Trash2 size={11} />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
